import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { auth } from '../firebase/firebase';
import { signInWithCustomToken } from 'firebase/auth';

const AuthCallback = () => {
  const navigate = useNavigate();

  useEffect(() => {
    if (window.hasNavigated) return;
    window.hasNavigated = true;

    const params = new URLSearchParams(window.location.search);
    console.log("🔍 AuthCallback - URL params:", Object.fromEntries(params.entries()));

    const success = params.get("success");
    const error = params.get("error");
    const isAdding = params.get("add")?.toLowerCase() === "true";
    const token = params.get("token");
    const email = params.get("email");
    const accessToken = params.get("accessToken");
    // const refreshToken = params.get("refreshToken"); // Not used in v2

    const handleError = (msg = "Authentication failed") => {
      console.error("❌ AuthCallback Error:", msg);
      message.error(msg);

      setTimeout(() => {
        if (window.opener) {
          window.opener.postMessage("account_add_error", "*");
          window.close();
        } else {
          navigate("/manage-accounts");
        }
      }, 1000);
    };

    const handleSuccess = () => {
      console.log("✅ Gmail account added successfully");
      message.success("Gmail account added successfully!");
      
      if (window.opener) {
        window.opener.postMessage("account_added", "*");
        setTimeout(() => {
          if (!window.closed) window.close();
        }, 300);
      } else {
        navigate("/manage-accounts", { state: { accountAdded: true } });
      }
    };

    // Handle errors first
    if (error) {
      return handleError(`Authentication failed: ${error}`);
    }

    // Handle Gmail account addition (v1 - DEPRECATED, use v2)
    if (success === "true" && isAdding && email && accessToken) {
      console.log("⚠️ Gmail v1 - Deprecated flow detected, redirecting to v2");
      console.log("🔍 Available params:", { success, isAdding, email, accessToken, token });
      
      // Redirect to v2 callback
      const v2Url = `${window.location.origin}/auth/callback-v2?success=true&add=true&email=${encodeURIComponent(email)}`;
      window.location.href = v2Url;
      return;
    }
    // Handle main login flow
    else if (success === "true" && token) {
      console.log("🟢 Main login flow - Firebase custom token received");
      console.log("🔍 Token (first 50 chars):", token.substring(0, 50) + "...");
      console.log("🔍 Current auth state:", auth.currentUser?.email || "No user");
      
      // ✅ CRITICAL FIX: Sign in to Firebase with custom token AND wait for auth state
      (async () => {
        try {
          console.log("🔐 Signing in to Firebase with custom token...");
          console.log("🔍 Firebase auth object:", auth);
          
          const userCredential = await signInWithCustomToken(auth, token);
          console.log("✅ Firebase sign-in successful!");
          console.log("✅ User email:", userCredential.user.email);
          console.log("✅ User UID:", userCredential.user.uid);
          
          // Get Firebase ID token and save to localStorage
          const idToken = await userCredential.user.getIdToken();
          localStorage.setItem("authToken", idToken);
          console.log("✅ Firebase ID token saved to localStorage");
          console.log("🔍 ID Token (first 50 chars):", idToken.substring(0, 50) + "...");
          
          // ✅ CRITICAL: Wait for onAuthStateChanged to fire and update AuthContext
          // This ensures ProtectedRoute will see the user
          console.log("⏳ Waiting for AuthContext to update...");
          console.log("⏳ Current auth.currentUser:", auth.currentUser?.email);
          
          await new Promise((resolve) => {
            const unsubscribe = auth.onAuthStateChanged((user) => {
              console.log("🔔 onAuthStateChanged fired in AuthCallback, user:", user?.email || "null");
              if (user) {
                console.log("✅ AuthContext updated - user recognized:", user.email);
                unsubscribe();
                resolve();
              }
            });
            
            // Timeout fallback (shouldn't happen, but just in case)
            setTimeout(() => {
              console.warn("⚠️ Auth state update timeout after 3s");
              console.warn("⚠️ Current user:", auth.currentUser?.email || "still null");
              unsubscribe();
              resolve();
            }, 3000);
          });
          
          console.log("✅ Auth state confirmed, proceeding with navigation");
          console.log("✅ Final auth.currentUser check:", auth.currentUser?.email);
          
          // ✅ ChatGPT Landing: Check if returning from chat
          const chatState = sessionStorage.getItem('chatState');
          if (chatState) {
            try {
              const { returnTo, draftCampaign } = JSON.parse(chatState);
              
              if (returnTo === 'chat-campaign-creation' && draftCampaign) {
                console.log("🎨 [ChatGPT] Restoring chat state and navigating to campaign creation");
                
                // Restore draft campaign
                sessionStorage.setItem('preFillCampaign', JSON.stringify(draftCampaign));
                
                // Clear chat state
                sessionStorage.removeItem('chatState');
                
                // Redirect to campaign creation (AFC uses /campaigns not /email-campaigns)
                message.success("Logged in! Creating your campaign...");
                setTimeout(() => navigate("/campaigns?openModal=true"), 500);
                return;
              }
            } catch (e) {
              console.error('Failed to restore chat state:', e);
            }
          }
          
          // Default: go to dashboard
          console.log("🚀 Navigating to dashboard...");
          message.success("Logged in successfully!");
          setTimeout(() => navigate("/dashboard"), 500);
          
        } catch (firebaseError) {
          console.error("❌ Firebase sign-in FAILED:", firebaseError);
          console.error("❌ Error code:", firebaseError.code);
          console.error("❌ Error message:", firebaseError.message);
          console.error("❌ Full error:", JSON.stringify(firebaseError, null, 2));
          message.error(`Sign-in failed: ${firebaseError.message}`);
          setTimeout(() => navigate("/login"), 1000);
        }
      })();
      
      return; // Prevent further execution while async operation completes
    }
    // Invalid response
    else {
      console.warn("⚠️ Invalid authentication response");
      handleError("Invalid authentication response.");
    }

  }, [navigate]);

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      backgroundColor: '#f0f2f5'
    }}>
      <div style={{ textAlign: 'center' }}>
        <h2>Completing authentication...</h2>
        <p>Please wait while we redirect you.</p>
      </div>
    </div>
  );
};

export default AuthCallback;